import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Select,
  Text,
  Title,
} from "@mantine/core";
import { useDebouncedState } from "@mantine/hooks";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import * as React from "react";
import { useEffect, useState } from "react";
import { repositoryMaster, repositoryPos } from "../../_base/_const/_constVar";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { _localization } from "../../config/location";
import { MessageResponse } from "../../model/MessageResponse";
import {
  EmployeeShortInfo,
  UserMasterModel,
} from "../../model/UserMasterModel";

/**
 * Chọn tài khoản cho một nhóm quyền, mở từ màn Nhóm người dùng.
 *
 * Đây là chiều ngược của UserAddGroup: bên kia chọn nhiều nhóm cho một tài khoản,
 * còn ở đây chọn nhiều tài khoản cho một nhóm.
 *
 * Người đã ở trong nhóm được tích sẵn. Bỏ tích rồi lưu là gỡ khỏi nhóm,
 * các nhóm khác của tài khoản đó không bị ảnh hưởng.
 */
const GroupAddUser = ({
  idGroup,
  groupName,
}: {
  idGroup: string;
  groupName?: string;
}) => {
  const [data, setData] = useState<UserMasterModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [keySearch, setKeySearch] = useDebouncedState<string>("", 400);
  const [depFilter, setDepFilter] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Lấy song song ba nguồn rồi ghép lại:
   * danh sách tài khoản (master), người đang trong nhóm (master), thông tin nhân viên (POS).
   *
   * Phòng ban và chức vụ chỉ để người phân quyền nhìn cho dễ nhận ra ai là ai,
   * nên POS hỏng thì bỏ qua, không chặn việc chọn.
   */
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [userRes, memberRes] = await Promise.all([
        repositoryMaster.get<MessageResponse<UserMasterModel[]>>(
          "/api/v1/auth/get-list?Skip=0&Take=100000&Sort=&IsOrder=false",
        ),
        repositoryMaster.get<MessageResponse<number[]>>(
          `/api/v1/auth/get-users-of-group?groupId=${idGroup}`,
        ),
      ]);

      if (!userRes || !userRes.success) {
        NotificationExtension.Fails("Không lấy được danh sách tài khoản !");
        return;
      }

      let users = userRes.data ?? [];

      try {
        const empRes = await repositoryPos.get<
          MessageResponse<EmployeeShortInfo[]>
        >("/api/v1/TblDmEmployee/get-all-info");

        if (empRes && empRes.success && empRes.data) {
          const byId = new Map(empRes.data.map((e) => [e.id, e]));
          users = users.map((u) => {
            const emp = u.empid ? byId.get(u.empid) : undefined;
            if (!emp) return u;
            return {
              ...u,
              empCode: emp.code,
              depName: emp.depName,
              roleName: emp.roleName,
              managerName: emp.managerName,
            };
          });
        }
      } catch (error) {
        console.error("Không lấy được thông tin nhân viên từ POS", error);
      }

      const memberIds = memberRes?.data ?? [];

      // Đưa người đã ở trong nhóm lên đầu để nhìn phát biết ngay nhóm đang có ai
      const isMember = (u: UserMasterModel) => memberIds.includes(u.id);
      users = [...users].sort((a, b) => Number(isMember(b)) - Number(isMember(a)));

      const selected: MRT_RowSelectionState = {};
      memberIds.forEach((id) => {
        selected[id.toString()] = true;
      });

      setData(users);
      setRowSelection(selected);
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi khi tải dữ liệu !");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!idGroup || idGroup.length < 1) {
      NotificationExtension.Fails("Nhóm quyền rỗng !");
      return;
    }

    setIsSaving(true);
    try {
      const res = await repositoryMaster.post<MessageResponse<boolean>>(
        "/api/v1/auth/update-users-of-group",
        {
          groupId: Number(idGroup),
          userIds: Object.keys(rowSelection).map((id) => Number(id)),
        },
      );

      if (res && res.success) {
        NotificationExtension.Success("Cập nhật nhân viên của nhóm thành công !");
      } else {
        NotificationExtension.Fails("Cập nhật thất bại !");
      }
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Có lỗi khi cập nhật !");
    } finally {
      setIsSaving(false);
    }
  };

  /** Lấy các giá trị khác nhau của một trường, bỏ trống, sắp theo bảng chữ cái */
  const distinctOf = (
    list: UserMasterModel[],
    pick: (u: UserMasterModel) => string | undefined,
  ) =>
    Array.from(
      new Set(list.map(pick).filter((v): v is string => !!v && v.length > 0)),
    ).sort((a, b) => a.localeCompare(b, "vi"));

  const depOptions = React.useMemo(
    () => distinctOf(data, (u) => u.depName),
    [data],
  );

  // Chức vụ thu hẹp theo phòng ban đang chọn, để không phải lội qua danh sách
  // chức vụ của cả công ty khi đã biết mình cần phòng nào
  const roleOptions = React.useMemo(
    () =>
      distinctOf(
        depFilter ? data.filter((u) => u.depName === depFilter) : data,
        (u) => u.roleName,
      ),
    [data, depFilter],
  );

  // Đổi phòng ban mà chức vụ đang chọn không còn trong phòng đó thì bỏ chọn chức vụ,
  // tránh lọc ra danh sách rỗng mà người dùng không hiểu vì sao
  useEffect(() => {
    if (roleFilter && !roleOptions.includes(roleFilter)) setRoleFilter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleOptions]);

  const dataView = React.useMemo(() => {
    const key = keySearch.trim().toLowerCase();

    return data.filter((u) => {
      if (depFilter && u.depName !== depFilter) return false;
      if (roleFilter && u.roleName !== roleFilter) return false;
      if (key.length < 1) return true;

      return [u.userName, u.fullName, u.empCode, u.depName, u.roleName]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(key));
    });
  }, [data, keySearch, depFilter, roleFilter]);

  const columns = React.useMemo<MRT_ColumnDef<UserMasterModel>[]>(
    () => [
      {
        accessorKey: "userName",
        header: "Tên đăng nhập",
        Cell: ({ renderedCellValue }) => (
          <Text fw={600} size="sm">
            {renderedCellValue}
          </Text>
        ),
      },
      {
        accessorKey: "fullName",
        header: "Họ và tên",
      },
      {
        accessorKey: "empCode",
        header: "Mã nhân viên",
      },
      {
        accessorKey: "depName",
        header: "Phòng ban",
      },
      {
        accessorKey: "roleName",
        header: "Chức vụ",
      },
      {
        accessorKey: "managerName",
        header: "Người quản lý trực tiếp",
      },
      {
        accessorKey: "inActive",
        header: "Trạng thái",
        size: 130,
        Cell: ({ row }) => (
          <Badge color={row.original.inActive ? "teal" : "red"}>
            {row.original.inActive ? "Đang hoạt động" : "Dừng hoạt động"}
          </Badge>
        ),
      },
    ],
    [],
  );

  const table = useMantineReactTable({
    columns,
    data: dataView,
    positionToolbarAlertBanner: "bottom",
    enableRowSelection: true,
    // Mã tài khoản làm khoá chọn, để tích sẵn được theo danh sách người đang ở trong nhóm
    getRowId: (row) => row.id?.toString(),
    enableTopToolbar: false,
    enableColumnActions: false,
    // Hàng gọn lại, chỉ cao vừa đủ chứa nội dung để nhìn được nhiều người một lúc
    initialState: {
      density: "xs",
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    mantineTableBodyCellProps: {
      style: { paddingTop: 4, paddingBottom: 4 },
    },
    mantineTableHeadCellProps: {
      style: { paddingTop: 6, paddingBottom: 6 },
    },
    enablePagination: true,
    // Tích ở dòng tiêu đề chọn toàn bộ kết quả đang lọc, không chỉ trang hiện tại.
    // Có vậy mới thêm được cả phòng ban vào nhóm bằng một lần tích.
    selectAllMode: "all",
    mantinePaginationProps: {
      rowsPerPageOptions: ["20", "50", "100"],
      withEdges: true,
    },
    localization: _localization,
    state: {
      isLoading,
      rowSelection,
      showProgressBars: isLoading,
    },
    onRowSelectionChange: setRowSelection,
    // Tách bảng ra khỏi ô tìm kiếm phía trên
    mantinePaperProps: {
      style: { marginTop: 14 },
    },
    // Chiều cao cố định: ít dữ liệu hay nhiều dữ liệu thì khung bảng vẫn y nguyên,
    // hộp thoại không co giãn mỗi lần đổi bộ lọc
    mantineTableContainerProps: {
      style: { height: "60vh" },
    },
  });

  const selectedCount = Object.keys(rowSelection).length;

  return (
    <Box>
      <Flex
        justify="space-between"
        align="center"
        mt="xs"
        mb="md"
        gap="md"
        wrap="wrap"
      >
        <Box>
          <Title order={6}>{groupName ?? "Nhóm quyền"}</Title>
          <Text size="sm" c="dimmed">
            Đã chọn {selectedCount} tài khoản
          </Text>
        </Box>

        <Flex gap="sm" align="center" wrap="wrap">
          <Select
            w={230}
            placeholder="Phòng ban"
            data={depOptions}
            value={depFilter}
            onChange={setDepFilter}
            searchable
            clearable
            nothingFoundMessage="Không có phòng ban"
          />
          <Select
            w={210}
            placeholder="Chức vụ"
            data={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            searchable
            clearable
            nothingFoundMessage="Không có chức vụ"
          />
          <Input
            w={260}
            placeholder="Tìm tên đăng nhập, họ tên, phòng ban ..."
            leftSection={<IconSearch size={16} />}
            defaultValue={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
          />
          <Button
            leftSection={<IconCheck size={18} />}
            color="blue"
            loading={isSaving}
            onClick={handleSave}
          >
            Lưu
          </Button>
        </Flex>
      </Flex>

      <MantineReactTable table={table} />
    </Box>
  );
};

export default GroupAddUser;
